<template>
  <div class="shipment-form-component">
    <form @submit.prevent>
      <div class="row">
        <div class="col-12">
          <legend class="legend-title-content">
            <span class="legend legend-title modal-edit">{{ $t('invoices.details.editItem.labelItem') }}</span>
          </legend>
        </div>

        <div class="col-lg-12 col-md-3 col-sm-3">
          <div class="form-group">
            <label for="bank">{{ $t('invoices.modalBankData.bankName') }} *</label>
            <ct-select
              name="bank"
              id="bank"
              class="from-control"
              :class="{'is-invalid': errors.has('bank')}"
              :data-vv-as="$t('invoices.modalBankData.bankName')"
              v-model="mutableItem.shipment.bank"
              :options="banks"
              :multiple="false"
              v-validate="{ required: true }"
              :disabled="notEditing"
            />
            <div class="invalid-feedback">{{ errors.first('bank') }}</div>
          </div>
        </div>

        <div class="col-lg-2 col-md-3 col-sm-3">
          <div class="form-group">
            <label for="agency">{{ $t('invoices.modalBankData.agency') }} *</label>
            <input
              type="text"
              class="form-control"
              id="agency"
              name="agency"
              v-model="mutableItem.shipment.agency"
              :placeholder="$t('invoices.modalBankData.agency')"
              v-validate="{ required: true }"
              @input="changeAgency"
              maxlength="6"
              :class="{'is-invalid': errors.has('agency')}"
              :data-vv-as="$t('invoices.modalBankData.agency')"
              :disabled="notEditing"
            />
            <div class="invalid-feedback">{{ errors.first('agency') }}</div>
          </div>
        </div>

        <div class="col-lg-4 col-md-3 col-sm-3">
          <div class="form-group">
            <label for="account">{{ $t('invoices.modalBankData.account') }} *</label>
            <input
              type="text"
              class="form-control"
              id="account"
              name="account"
              v-model="mutableItem.shipment.account"
              :placeholder="$t('invoices.modalBankData.account')"
              v-validate="{ required: true }"
              @input="applyAccountMask(false)"
              @change="changeAccount"
              maxlength="13"
              :class="{'is-invalid': errors.has('account')}"
              :data-vv-as="$t('invoices.modalBankData.account')"
              :disabled="notEditing"
            />
            <div class="invalid-feedback">{{ errors.first('account') }}</div>
          </div>
        </div>

        <div class="col-lg-3 col-md-3 col-sm-3">
          <div class="form-group">
            <label for="kind">{{ $t('invoices.modalBankData.kindAccount') }} *</label>
            <ct-select
              name="kind"
              id="kind"
              class="from-control"
              :class="{'is-invalid': errors.has('kind')}"
              :data-vv-as="$t('invoices.modalBankData.kindAccount')"
              v-model="mutableItem.shipment.kind"
              :options="accountTypes"
              :multiple="false"
              v-validate="{ required: true }"
              :disabled="notEditing"
            />
            <div class="invalid-feedback">{{ errors.first('kind') }}</div>
          </div>
        </div>

        <div class="col-lg-3 col-md-3 col-sm-3" v-if="isCaixa">
          <div class="form-group">
            <label for="operation">{{ $t('invoices.modalBankData.operation') }} *</label>
            <input
              type="text"
              class="form-control"
              id="operation"
              name="operation"
              v-model="mutableItem.shipment.operation"
              :placeholder="$t('invoices.modalBankData.operation')"
              v-validate="{ required: true }"
              @change="changeOperation"
              :class="{'is-invalid': errors.has('operation')}"
              :data-vv-as="$t('invoices.modalBankData.operation')"
              :disabled="notEditing"
            />
            <div class="invalid-feedback">{{ errors.first('operation') }}</div>
          </div>
        </div>

        <div class="col-lg-12 col-md-12 col-sm-12 col-xs-12 check-group">
          <div class="form-group">
            <div class="checkbox" :class="{'checked': mutableItem.shipment.isThirdParty}">
              <input type="checkbox" :value="true" v-model="mutableItem.shipment.isThirdParty" />
            </div>
            <label>{{ $t('invoices.modalBankData.isThirdParty') }}</label>
          </div>
        </div>
      </div>

      <div class="row third-party-data" v-if="mutableItem.shipment.isThirdParty">
        <div class="col-lg-3 col-md-6 col-sm-12 col-xs-12">
          <div class="form-group">
            <label>{{ $t('invoices.modalBankData.name') }}</label>
            <input
              type="text"
              class="form-control"
              id="name"
              name="name"
              v-model.trim="mutableItem.shipment.name"
              :placeholder="$t('invoices.modalBankData.name')"
              v-validate="{ required: true }"
              :class="{'is-invalid': errors.has('name')}"
              :data-vv-as="$t('invoices.modalBankData.name')"
            />
            <div class="invalid-feedback">{{ errors.first('name') }}</div>
          </div>
        </div>
        <div class="col-lg-2 col-md-6 col-sm-12 col-xs-12">
          <div class="form-group">
            <label>{{ $t('invoices.modalBankData.document') }}</label>
            <the-mask
              type="text"
              class="form-control"
              id="document"
              name="document"
              :mask="documentMask"
              :masked="false"
              v-model.trim="mutableItem.shipment.document"
              :placeholder="$t('invoices.modalBankData.document')"
              v-validate="{ required: true }"
              :class="{'is-invalid': errors.has('document')}"
              :data-vv-as="$t('invoices.modalBankData.document')"
            />
            <div class="invalid-feedback">{{ errors.first('document') }}</div>
          </div>
        </div>
        <div class="col-lg-3 col-md-6 col-sm-12 col-xs-12">
          <div class="form-group">
            <label>{{ $t('invoices.modalBankData.thirdPartyEmail') }}</label>
            <input
              type="text"
              class="form-control"
              id="email"
              name="email"
              v-model.trim="mutableItem.shipment.thirdParty.email"
              :placeholder="$t('invoices.modalBankData.thirdPartyEmail')"
              v-validate="{ required: true, email: true }"
              :class="{'is-invalid': errors.has('email')}"
              :data-vv-as="$t('invoices.modalBankData.thirdPartyEmail')"
            />
            <div class="invalid-feedback">{{ errors.first('email') }}</div>
          </div>
        </div>
        <div class="col-lg-4 col-md-6 col-sm-12 col-xs-12">
          <div class="form-group">
            <label>{{ $t('invoices.modalBankData.thirdPartyPurpose') }}</label>
            <ct-select
              name="purpose"
              id="purpose"
              class="from-control"
              v-model="mutableItem.shipment.thirdParty.purpose"
              :reduce="purpose => purpose.value"
              :placeholder="$t('invoices.modalBankData.thirdPartyPurpose')"
              v-validate="{ required: true }"
              :class="{'is-invalid': errors.has('purpose')}"
              :data-vv-as="$t('invoices.modalBankData.thirdPartyPurpose')"
              :options="purposes"
            />
            <div class="invalid-feedback">{{ errors.first('purpose') }}</div>
          </div>
        </div>
      </div>

      <ct-item-table-events
        slot="body"
        :events="item.events || []"
      />

      <div class="row">
        <div class="col-xs-12 col-sm-12">
          <div class="form-group pull-right form-inline">
            <button class="btn btn-default btn-lg" type="button" @click="reset">
              {{ $t('invoices.details.editItem.cancel') }}
            </button>
            <button :disabled="notEditing" class="btn btn-primary btn-lg" type="submit" @click="save" >
              {{ $t('invoices.details.editItem.confirm') }}
            </button>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { TheMask } from 'vue-the-mask';
import { CtSelect } from '@citartech/core-ui';
import CtItemTableEvents from '@/components/Invoices/InvoiceDetails/EditItem/ItemTableEvents.vue';

export default {
  name: 'EditShipmentForm',
  data: () => ({
    mutableItem: {},
  }),
  components: {
    CtSelect,
    CtItemTableEvents,
    TheMask,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    notEditing: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState({
      banks: state => state.selects.banks || {},
      accountTypes: state => state.selects.accountTypes || {},
      purposes: state => state.selects.purposes || {},
    }),
    isCaixa () {
      return this.mutableItem.shipment && String(this.mutableItem.shipment.bank) === '104';
    },
    documentMask () {
      return ['###.###.###-##', '##.###.###/####-##'];
    },
  },
  methods: {
    ...mapActions([
      'getBankList',
    ]),
    async changeAgency () {
      this.applyAgencyMask();
    },
    async changeAccount () {
      this.applyAccountMask(true);
    },
    changeOperation () {
      const operation = this.mutableItem.shipment.operation;
      if (operation && operation.length < 3) {
        this.mutableItem.shipment.operation = operation.toString().padStart(3, '0');
      }
    },
    applyAgencyMask () {
      const agency = this.mutableItem.shipment.agency.replace(/[^\d]/g, '');
      if (agency && agency.length > 4) {
        this.mutableItem.shipment.agency = [
          agency.slice(0, agency.length - 1),
          '-',
          agency.slice(agency.length - 1),
        ].join('');
      }
    },
    applyAccountMask (changed = false) {
      const account = this.mutableItem.shipment.account.replace(/[^\d]/g, '');
      const position = account.length - 1;
      if (account.length > 11 || changed) {
        this.mutableItem.shipment.account = [
          account.slice(0, position),
          '-',
          account.slice(position),
        ].join('');
      }
    },
    async reset () {
      this.$emit('reset');
    },
    async save () {
      if (await this.$validator.validate()) {
        const data = { shipment: this.mutableItem.shipment };
        this.$emit('save', data);
      }
    },
  },
  async created () {
    this.mutableItem = JSON.parse(JSON.stringify(this.item));
    await this.getBankList();
  },
};
</script>

<style lang="scss">
.legend-title-content {
  margin-bottom: 20px;
}
.check-group {
  .checkbox {
    display: inline-block;
    margin-right: 5px;
  }
}
</style>
